import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { CreateAgentFormType, createAgentSchema } from "../schema/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import AgentAgencyForm from "./AgentAgencyForm";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import SearchComponent from "./SearchComponent";
import { UserObj } from "@/types/interfaces/common";
import axiosInstance from "@/services/api/axiosInstance";
import { toast } from "sonner";
import StickyFooter from "@/components/StickyFooter/StickyFooter";
import Button from "@mui/joy/Button";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import LinkButton from "@/components/LinkButton/LinkButton";
import { ADMIN_ROUTE_PREFIX } from "@/types/constants/common";

const CreateAgentForm = () => {
  const [agent, setAgent] = useState<UserObj | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (agent !== null) {
      const {
        firstname,
        lastname,
        ttandEmail,
        businessPhone,
        agentId,
        clientlinqBranch,
        city,
        province,
        postalCode,
        streetAddress,
        brandedAs,
      } = agent;
      setValue("firstname", firstname ?? "");
      setValue("lastname", lastname ?? "");
      setValue("ttandEmail", ttandEmail ?? "");
      setValue("businessPhone", businessPhone?.replace(/-/g, "") ?? "");
      setValue("city", city ?? "");
      setValue("province", province ?? "");
      setValue("postalCode", postalCode ?? "");
      setValue("streetAddress", streetAddress ?? "");
      setValue("brandedAs", brandedAs ?? "");
      setValue("agentId", `${agentId}`);
      setValue("clientlinqBranch", clientlinqBranch);
    }
  }, [agent]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<CreateAgentFormType>({
    resolver: zodResolver(createAgentSchema),
    defaultValues: {
      firstname: "",
      lastname: "",
      streetAddress: "",
      city: "",
      province: "",
      postalCode: "",
      ttandEmail: "",
      businessPhone: "",
      brandedAs: "",
      agentId: "",
      clientlinqBranch: "",
    },
  });

  const onSubmit = async (data: CreateAgentFormType) => {
    setIsSubmitting(true);
    try {
      // format for createAgent -> constria
      const formatedData = {
        SourceAgencyId: data.clientlinqBranch,
        FirstName: data.firstname,
        LastName: data.lastname,
        EmailAddress: data.ttandEmail,
        Phone: data.businessPhone,
        Role: "Agent",
        SourceAgentId: data.agentId,
        Address: {
          BusinessName: data.brandedAs,
          Address1: data.streetAddress,
          Address2: "",
          City: data.city,
          State: data.province,
          PostalCode: data.postalCode,
          Country: "CA",
          Website: "",
        },
      };
      const result = await axiosInstance.post(`/admin/constria/create-agent`, formatedData);
      toast.success(result.data?.message || "Agent Created Successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something Went Wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack>
      <Typography
        level="h3"
        sx={{
          mb: 4,
        }}
      >
        {"Create Agent"}
      </Typography>
      <SearchComponent type={"agent"} onDataFetched={setAgent} mode={"create"} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={"row"} gap={4}>
          <AgentAgencyForm
            mode={"create"}
            type={"agent"}
            register={register}
            control={control}
            setValue={setValue}
            setError={setError}
            errors={errors}
            disable={false}
          />
        </Stack>
        <StickyFooter sx={{ mt: 4 }}>
          <Button
            type="submit"
            sx={{ textTransform: "capitalize" }}
            loading={isSubmitting}
            disabled={isSubmitting}
            startDecorator={<AddOutlinedIcon />}
          >
            Create Agent
          </Button>
          <LinkButton color="neutral" variant="outlined" to={`${ADMIN_ROUTE_PREFIX}/`} disabled={isSubmitting}>
            Cancel
          </LinkButton>
        </StickyFooter>
      </form>
    </Stack>
  );
};

export default CreateAgentForm;
